import React, { lazy, Suspense, useState, useEffect, useRef, createContext, useContext } from 'react';
import { Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import StorefrontNavbar from './scenes/global/StorefrontNavbar';
import StorefrontFooter from './scenes/global/StorefrontFooter';
import CartDrawer from './components/cart/CartDrawer';
import LoadingSpinner from './components/common/LoadingSpinner';
import { useAuth } from './context/AuthContext';

// Public pages
const HomePage = lazy(() => import('./scenes/storefront/HomePage'));
const StoreHomePage = lazy(() => import('./scenes/storefront/StoreHomePage'));
const ProductListPage = lazy(() => import('./scenes/storefront/ProductListPage'));
const ProductDetailPage = lazy(() => import('./scenes/storefront/ProductDetailPage'));
const CategoryPage = lazy(() => import('./scenes/storefront/CategoryPage'));
const SearchResultsPage = lazy(() => import('./scenes/storefront/SearchResultsPage'));
const CartPage = lazy(() => import('./scenes/cart/CartPage'));
const CheckoutPage = lazy(() => import('./scenes/cart/CheckoutPage'));
const OrderConfirmation = lazy(() => import('./scenes/cart/OrderConfirmation'));
const LandingPageRenderer = lazy(() => import('./scenes/landing/LandingPageRenderer'));
const DonationPage = lazy(() => import('./scenes/cart/DonationPage'));
const ProductStoreRedirect = lazy(() => import('./scenes/storefront/ProductStoreRedirect'));
const PosRegister = lazy(() => import('./scenes/pos/PosRegister'));

// Admin pages
const Login = lazy(() => import('./scenes/login/index'));
const SellerRegistration = lazy(() => import('./scenes/login/SellerRegistration'));
const AdminDashboard = lazy(() => import('./scenes/admin/AdminDashboard'));
const ProductManagement = lazy(() => import('./scenes/admin/ProductManagement'));
const CategoryManagement = lazy(() => import('./scenes/admin/CategoryManagement'));
const OrderManagement = lazy(() => import('./scenes/admin/OrderManagement'));
const OrderDetail = lazy(() => import('./scenes/admin/OrderDetail'));
const InventoryManagement = lazy(() => import('./scenes/admin/InventoryManagement'));
const StripeSettings = lazy(() => import('./scenes/admin/StripeSettings'));
const LandingPageAdmin = lazy(() => import('./scenes/admin/LandingPageAdmin'));
const LandingPageBuilder = lazy(() => import('./scenes/admin/LandingPageBuilder'));
const StoreManagement = lazy(() => import('./scenes/admin/StoreManagement'));
const UserManagement = lazy(() => import('./scenes/admin/UserManagement'));
const PickListReport = lazy(() => import('./scenes/admin/PickListReport'));
const CouponManagement = lazy(() => import('./scenes/admin/CouponManagement'));

// Admin layout components
const AdminSidebar = lazy(() => import('./scenes/global/AdminSidebar'));
const AdminTopbar = lazy(() => import('./scenes/global/AdminTopbar'));

// Sidebar context for admin layout
const SidebarContext = createContext({ open: true, toggle: () => {} });
export const useSidebar = () => useContext(SidebarContext);

function ProtectedRoute({ children, requiredRole }) {
  const { isAuthenticated, user } = useAuth();
  if (!isAuthenticated) return <Navigate to="/admin/login" replace />;
  if (requiredRole && user?.Role !== requiredRole && user?.Role !== 'SuperAdmin') return <Navigate to="/admin" replace />;
  return children;
}

function AdminLayout({ children, requiredRole }) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const toggleSidebar = () => setSidebarOpen(prev => !prev);

  return (
    <ProtectedRoute requiredRole={requiredRole}>
      <SidebarContext.Provider value={{ open: sidebarOpen, toggle: toggleSidebar, setOpen: setSidebarOpen }}>
        <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f5f7fa' }}>
          <Suspense fallback={null}>
            <AdminSidebar />
          </Suspense>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <Suspense fallback={null}>
              <AdminTopbar />
            </Suspense>
            <Box
              component="main"
              sx={{
                flex: 1,
                p: { xs: 2, sm: 3 },
                overflow: 'auto',
                maxWidth: '100%',
              }}
            >
              {children}
            </Box>
          </Box>
        </Box>
      </SidebarContext.Provider>
    </ProtectedRoute>
  );
}

function StorefrontLayout({ children }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <StorefrontNavbar />
      <CartDrawer />
      <Box sx={{ flex: 1 }}>{children}</Box>
      <StorefrontFooter />
    </Box>
  );
}

// Wrapper that passes storeSlug to storefront pages
function StoreStorefrontLayout({ children }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <StorefrontNavbar />
      <CartDrawer />
      <Box sx={{ flex: 1 }}>{children}</Box>
      <StorefrontFooter />
    </Box>
  );
}

function MetaPixelPageViewTracker() {
  const location = useLocation();
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
      window.fbq('track', 'PageView');
    }
  }, [location.pathname, location.search]);
  return null;
}

function App() {
  return (
    <Suspense fallback={<LoadingSpinner fullPage />}>
      <MetaPixelPageViewTracker />
      <Routes>
        {/* Landing pages & donation - standalone */}
        <Route path="/landing/:slug" element={<LandingPageRenderer />} />
        <Route path="/donate" element={<DonationPage />} />

        {/* Auth pages */}
        <Route path="/admin/login" element={<Login />} />
        <Route path="/seller/register" element={<SellerRegistration />} />

        {/* Admin routes */}
        <Route path="/admin" element={<AdminLayout><AdminDashboard /></AdminLayout>} />
        <Route path="/admin/products" element={<AdminLayout><ProductManagement /></AdminLayout>} />
        <Route path="/admin/categories" element={<AdminLayout><CategoryManagement /></AdminLayout>} />
        <Route path="/admin/orders" element={<AdminLayout><OrderManagement /></AdminLayout>} />
        <Route path="/admin/orders/:apiid" element={<AdminLayout><OrderDetail /></AdminLayout>} />
        <Route path="/admin/inventory" element={<AdminLayout><InventoryManagement /></AdminLayout>} />
        <Route path="/admin/stripe" element={<AdminLayout><StripeSettings /></AdminLayout>} />
        <Route path="/admin/landing-pages" element={<AdminLayout><LandingPageAdmin /></AdminLayout>} />
        <Route path="/admin/landing-pages/:apiid" element={<AdminLayout><LandingPageBuilder /></AdminLayout>} />
        <Route path="/admin/pick-list" element={<AdminLayout><PickListReport /></AdminLayout>} />
        <Route path="/admin/coupons" element={<AdminLayout><CouponManagement /></AdminLayout>} />

        {/* SuperAdmin-only routes */}
        <Route path="/admin/stores" element={<AdminLayout requiredRole="SuperAdmin"><StoreManagement /></AdminLayout>} />
        <Route path="/admin/users" element={<AdminLayout requiredRole="SuperAdmin"><UserManagement /></AdminLayout>} />

        {/* Mobile POS — full screen, no storefront chrome, PIN-gated */}
        <Route path="/store/:storeSlug/pos" element={<PosRegister />} />

        {/* Store-scoped public storefront */}
        <Route path="/store/:storeSlug" element={<StoreStorefrontLayout><StoreHomePage /></StoreStorefrontLayout>} />
        <Route path="/store/:storeSlug/products" element={<StoreStorefrontLayout><ProductListPage /></StoreStorefrontLayout>} />
        <Route path="/store/:storeSlug/products/:slug" element={<StoreStorefrontLayout><ProductDetailPage /></StoreStorefrontLayout>} />
        <Route path="/store/:storeSlug/cart" element={<StoreStorefrontLayout><CartPage /></StoreStorefrontLayout>} />
        <Route path="/store/:storeSlug/checkout" element={<StoreStorefrontLayout><CheckoutPage /></StoreStorefrontLayout>} />
        <Route path="/store/:storeSlug/order-confirmation/:orderId" element={<StoreStorefrontLayout><OrderConfirmation /></StoreStorefrontLayout>} />

        {/* Public storefront (marketplace) */}
        <Route path="/" element={<StorefrontLayout><HomePage /></StorefrontLayout>} />
        <Route path="/products" element={<Navigate to="/" replace />} />
        <Route path="/products/:slug" element={<StorefrontLayout><ProductStoreRedirect /></StorefrontLayout>} />
        <Route path="/category/:slug" element={<StorefrontLayout><CategoryPage /></StorefrontLayout>} />
        <Route path="/search" element={<StorefrontLayout><SearchResultsPage /></StorefrontLayout>} />
        <Route path="/cart" element={<StorefrontLayout><CartPage /></StorefrontLayout>} />
        <Route path="/checkout" element={<StorefrontLayout><CheckoutPage /></StorefrontLayout>} />
        <Route path="/order-confirmation/:orderId" element={<StorefrontLayout><OrderConfirmation /></StorefrontLayout>} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

export default App;
